import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import { Col, Form as FormAntd, Modal, Row } from 'antd'
import { Formik } from 'formik'
import moment from 'moment'
import { number, string, object } from 'yup'
import { SelectValue } from 'antd/lib/select'
import { useDispatch, useSelector } from 'react-redux'

import { Id, Request } from 'src/types'

import { ApplicationState } from 'src/store'
import { Button, Card } from 'src/components/Ant'
import {
  DatePickerRange,
  InputNumber,
  Radio,
  Select
} from 'src/components/Formik'
import { getCities } from 'src/core/store/ducks/cities/actions'
import { getHotels, resetHotels } from 'src/core/store/ducks/hotels/actions'
import { HotelSearchType } from 'src/core/store/ducks/hotels/types'
import { Option } from 'src/components/Formik/Select'
import { useStepsContext } from 'src/views/Requests/views/Form'
import { useT, stringify } from 'src/core/hooks/use-translation'

import { WrapTextSize } from './styles'

type Props = {
  request: Request
}

type SuggestionDate = {
  start_date?: number
  end_date?: number
}

const initialValues = {
  bedrooms: 1,
  checkin_date: moment()
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .unix(),
  checkout_date: moment()
    .add(7, 'days')
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .unix(),
  key: 0,
  trend_destination_id: '',
  trend_destination_name: '',
  type: HotelSearchType.SINGLE_PERIOD
}

function Form({ request }: Props): JSX.Element {
  const dispatch = useDispatch()

  const refTimeout = useRef<NodeJS.Timeout>(null)

  const { cities, hotels } = useSelector((state: ApplicationState) => ({
    cities: state.cities,
    hotels: state.hotels
  }))

  const [cityList, setCityList] = useState<Option[]>([])
  const [stretch, setStretch] = useState([])

  const { t } = useT()

  useEffect(() => {
    setCityList(
      cities.list.map<Option>(cityItem => ({
        label: `${cityItem.city_name} - ${cityItem.state_name}`,
        value: `${cityItem.trend_city_id}`
      }))
    )
  }, [cities.list])

  const stepsContext = useStepsContext()

  const participants = useMemo(() => {
    if (request.request_user_data) {
      return request.request_user_data.filter(user => user.included).length
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.request_user_data])

  const suggestionDate = useMemo<SuggestionDate>(() => {
    const nextMonday = moment()
      .add(moment().isoWeekday() > 4 ? 2 : 1, 'weeks')
      .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
      .isoWeekday(1)
    const momentRequestOn = moment.unix(request.requested_on)
    const start_date =
      nextMonday > momentRequestOn ? nextMonday : momentRequestOn
    const end_date = start_date.clone().add(1, 'd')
    return { start_date: start_date.unix(), end_date: end_date.unix() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.requested_on])

  const getCustomerSupplierId = useCallback((): Id => {
    if (request.full_request_hotel && request.full_request_hotel.length) {
      return request.full_request_hotel[0].customer_supplier_id
    }
    if (request.full_request_vehicle && request.full_request_vehicle.length) {
      return request.full_request_vehicle[0].customer_supplier_id
    }
    const serviceTicket = request.request_tickets.find(ticketItem =>
      ['AIR', 'HOTEL', 'VEHICLE'].includes(ticketItem.request_ticket_type)
    )

    if (serviceTicket) {
      if (serviceTicket.request_ticket_hotel_selected) {
        return serviceTicket.request_ticket_hotel_selected
          .request_quote_hotel_room.request_quote_hotel.request_quote
          .customer_supplier_id
      }
      if (serviceTicket.request_ticket_vehicle_selected) {
        return serviceTicket.request_ticket_vehicle_selected
          .request_quote_vehicle.request_quote.customer_supplier_id
      }
    }

    return null
  }, [
    request.full_request_hotel,
    request.full_request_vehicle,
    request.request_tickets
  ])
  const hotelsLoading = useMemo(() => {
    return Object.values(hotels.searches).some(({ loading }) => loading)
  }, [hotels.searches])

  return (
    <Formik
      initialValues={{
        ...initialValues,
        bedrooms: participants,
        checkin_date: suggestionDate.start_date,
        checkout_date: suggestionDate.end_date
      }}
      onSubmit={(values, formikActions) => {
        stepsContext.setRequest({
          ...request,
          full_request_hotel: []
        })

        if (values.type === HotelSearchType.MULTIPLE_PERIOD) {
          dispatch(resetHotels())
          setStretch([
            ...stretch,
            {
              ...values,
              key: !stretch.length ? 0 : stretch[stretch.length - 1].key + 1
            }
          ])
          formikActions.resetForm({
            ...initialValues,
            bedrooms: participants,
            checkin_date: suggestionDate.start_date,
            checkout_date: suggestionDate.end_date,
            type: HotelSearchType.MULTIPLE_PERIOD
          })
          return
        }
        const callSubmit = () => {
          let customer_supplier_id

          if (request.full_request_air && request.full_request_air.length) {
            customer_supplier_id =
              request.full_request_air[0].customer_supplier_id
          } else if (
            request.full_request_vehicle &&
            request.full_request_vehicle.length
          ) {
            customer_supplier_id =
              request.full_request_vehicle[0].customer_supplier_id
          } else {
            const serviceTicket = request.request_tickets.find(ticketItem =>
              ['AIR', 'HOTEL', 'VEHICLE'].includes(
                ticketItem.request_ticket_type
              )
            )

            if (serviceTicket) {
              if (serviceTicket.request_ticket_air_selected) {
                customer_supplier_id =
                  serviceTicket.request_ticket_air_selected
                    .request_quote_air_trip.request_quote_air_one_way
                    .request_quote.customer_supplier_id
              } else if (serviceTicket.request_ticket_vehicle_selected) {
                customer_supplier_id =
                  serviceTicket.request_ticket_vehicle_selected
                    .request_quote_vehicle.request_quote.customer_supplier_id
              }
            }
          }
          stepsContext.setBudgetAllocations({
            standard_type: 'HOTEL',
            tickets: []
          })
          dispatch(
            getHotels({
              customer_supplier_id,
              filters: {
                bedrooms: values.bedrooms,
                checkin_date: values.checkin_date,
                checkout_date: values.checkout_date,
                num_adults: request.request_user_data.length,
                trend_destination_id: values.trend_destination_id
              },
              reason_id:
                request.tmp_reason_id || request.request_user_data[0].reason_id,
              searchKey: values.trend_destination_name,
              searchType: HotelSearchType.SINGLE_PERIOD
            })
          )
        }

        if (request.full_request_hotel && request.full_request_hotel.length) {
          Modal.confirm({
            cancelText: t('common.actions.cancel'),
            content: t('web.services.hotels.alert-clear-search'),
            onOk: () => callSubmit()
          })
          return
        }

        callSubmit()
      }}
      validationSchema={object().shape({
        bedrooms: number()
          .max(
            request.request_user_data.length,
            stringify('common.yup.field-max-value', {
              value: request.request_user_data.length
            })
          )
          .required('common.required'),
        checkout_date: number().required('common.required'),
        trend_destination_id: string().trim().required('common.required'),
        checkin_date: number().required('common.required')
      })}
      render={formikProps => (
        <FormAntd onSubmit={formikProps.handleSubmit}>
          <Radio
            dataTestId="hotel-period"
            formikProps={formikProps}
            name="type"
            onChange={e => {
              const nextType = e.target.value
              formikProps.setFieldValue('type', nextType)
            }}
            options={[
              {
                label: 'web.services.hotels.itinerary.single-period',
                value: HotelSearchType.SINGLE_PERIOD
              },
              {
                label: 'web.services.hotels.itinerary.multi-period',
                value: HotelSearchType.MULTIPLE_PERIOD
              }
            ]}
            required={false}
          />
          <Row gutter={24}>
            <Col span={request.request_user_data.length > 1 ? 10 : 12}>
              <Select
                dataTestId="destination-list"
                filterOption={false}
                formikProps={formikProps}
                label="web.services.hotels.form.place.title"
                loading={cities.loading}
                name="trend_destination_id"
                onChange={(value: SelectValue) => {
                  const foundCity = cities.list.find(
                    ({ trend_city_id }) => `${trend_city_id}` === value
                  )

                  const cityName = !foundCity
                    ? ''
                    : `${foundCity.city_name} - ${foundCity.state_name}`

                  formikProps.setFieldValue('trend_destination_id', value)
                  formikProps.setFieldValue('trend_destination_name', cityName)
                }}
                onSearch={value => {
                  clearTimeout(refTimeout.current)
                  refTimeout.current = setTimeout(() => {
                    if (value) {
                      dispatch(getCities({ name: value }))
                    }
                  }, 500)
                }}
                options={cityList}
                placeholder="web.services.hotels.form.place.placeholder"
                showSearch
              />
            </Col>
            <Col span={request.request_user_data.length > 1 ? 10 : 12}>
              <DatePickerRange
                disabledDate={current =>
                  current && current < moment.unix(request.requested_on)
                }
                formikProps={formikProps}
                label="web.services.hotels.form.period.placeholder"
                name={['checkin_date', 'checkout_date']}
                placeholder={[
                  'web.services.hotels.form.period.start-time',
                  'web.services.hotels.form.period.end-time'
                ]}
                showTime={false}
              />
            </Col>
            {request.request_user_data.length > 1 && (
              <Col span={4}>
                <InputNumber
                  dataTestId="hotel-bedrooms"
                  formikProps={formikProps}
                  label="web.services.hotels.form.bedrooms"
                  max={request.request_user_data.length}
                  min={1}
                  name="bedrooms"
                />
              </Col>
            )}
          </Row>
          {formikProps.values.type === HotelSearchType.SINGLE_PERIOD ? (
            <FormAntd.Item>
              <Button
                block
                data-test-id="hotel-search-action"
                htmlType="submit"
                icon="search"
                loading={hotelsLoading}
                title="common.actions.search"
                type="primary"
                value="submit"
              />
            </FormAntd.Item>
          ) : (
            <FormAntd.Item>
              <Button
                block
                data-test-id="hotel-search-action"
                htmlType="submit"
                icon="plus"
                loading={hotelsLoading}
                title="common.actions.add"
                type="dashed"
              />
            </FormAntd.Item>
          )}
          {formikProps.values.type === HotelSearchType.MULTIPLE_PERIOD && (
            <>
              <Row gutter={24}>
                {stretch.map(stretchItem => (
                  <Col span={8} key={stretchItem.key}>
                    <Card>
                      <div
                        style={{
                          display: 'flex',
                          flex: 1,
                          flexDirection: 'row',
                          justifyContent: 'space-evenly'
                        }}
                      >
                        <div>
                          <WrapTextSize fontSize="16px">
                            {stretchItem.trend_destination_name}
                          </WrapTextSize>
                          <br />
                          <WrapTextSize fontSize="12px">
                            {`${moment
                              .unix(stretchItem.checkin_date)
                              .format('DD/MM/YYYY')} - ${moment
                              .unix(stretchItem.checkout_date)
                              .format('DD/MM/YYYY')}`}
                          </WrapTextSize>
                        </div>
                        <Button
                          data-test-id={`hotel-search-multi-remove-${stretchItem}`}
                          icon="delete"
                          onClick={() => {
                            setStretch(periods =>
                              periods.filter(
                                period => period.key !== stretchItem.key
                              )
                            )
                          }}
                          type="danger"
                        />
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
              {!!stretch.length && (
                <Button
                  data-test-id="hotel-search-multi-action"
                  style={{ marginTop: '10px' }}
                  title="common.actions.search"
                  icon="search"
                  type="primary"
                  loading={hotelsLoading}
                  onClick={() => {
                    dispatch(resetHotels())
                    stepsContext.setBudgetAllocations({
                      standard_type: 'HOTEL',
                      tickets: []
                    })
                    const customer_supplier_id = getCustomerSupplierId()
                    stretch.forEach((stretchItem, index) => {
                      dispatch(
                        getHotels({
                          customer_supplier_id,
                          filters: {
                            bedrooms: stretchItem.bedrooms,
                            checkin_date: stretchItem.checkin_date,
                            checkout_date: stretchItem.checkout_date,
                            num_adults: request.request_user_data.length,
                            trend_destination_id:
                              stretchItem.trend_destination_id
                          },
                          reason_id:
                            request.tmp_reason_id ||
                            request.request_user_data[0].reason_id,
                          searchKey: `${index + 1} - ${
                            stretchItem.trend_destination_name
                          }`,
                          searchType: HotelSearchType.MULTIPLE_PERIOD
                        })
                      )
                    })
                  }}
                />
              )}
            </>
          )}
        </FormAntd>
      )}
    />
  )
}

export default Form
